import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useTheme, callHandlerJson, IframeBridge } from '@alga-psa/extension-iframe-sdk';
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  LoadingIndicator,
  SearchInput,
  Stack,
  Text,
  Tooltip,
  type Column,
} from '@alga-psa/ui-kit';
import type { ApplianceListRow, ApplianceStatus, BadgeTone, Edition, EntitlementKind, InstallCodeState, ListEnvelope } from './types';
import {
  LicenseExpBadge,
  codeTone,
  fmtDate,
  fmtDateTime,
  fmtUnixDate,
  licenseHealth,
  licenseHealthMeta,
  relTime,
  statusTone,
  toneColor,
  type LicenseHealth,
} from './shared';
import { InstallDetail } from './detail';
import { CreateTenantDialog } from './actions';

// ── Facet helpers ────────────────────────────────────────────────────────────

type HealthClass = 'healthy' | 'stale' | 'never' | 'na';

// Classify a row's check-in health. Essentials tenants (and any tenant with no
// registered appliance) have no check-in signal → 'na'.
function healthClass(row: ApplianceListRow): HealthClass {
  if (row.edition === 'essentials' || row.appliance_count === 0) return 'na';
  if (!row.last_checkin_at) return 'never';
  const ageH = (Date.now() - new Date(row.last_checkin_at).getTime()) / 3600000;
  return ageH <= 48 ? 'healthy' : 'stale';
}

const healthFacetMeta: Record<HealthClass, { label: string; tone: BadgeTone; desc: string }> = {
  healthy: { label: 'Healthy', tone: 'success', desc: 'Checked in within the last 48h' },
  stale: { label: 'Stale >48h', tone: 'warning', desc: 'Last check-in older than 48h' },
  never: { label: 'Never', tone: 'danger', desc: 'Has appliance(s) but never checked in' },
  na: { label: 'N/A', tone: 'default', desc: 'No check-in expected (essentials or no appliance)' },
};

function checkinHealth(row: ApplianceListRow): { label: string; tone: BadgeTone } {
  const h = healthClass(row);
  if (h === 'na') return { label: 'n/a', tone: 'default' };
  if (h === 'never') return { label: 'never', tone: 'danger' };
  return { label: relTime(row.last_checkin_at), tone: healthFacetMeta[h].tone };
}

type EntitlementFacet = EntitlementKind | 'none';
const entitlementFacet = (r: ApplianceListRow): EntitlementFacet => r.entitlement_kind ?? 'none';

const statusOrder: ApplianceStatus[] = ['active', 'installed', 'registered', 'suspended', 'cancelled'];
const editionOrder: Edition[] = ['essentials', 'pro'];
const healthOrder: HealthClass[] = ['healthy', 'stale', 'never', 'na'];
const codeOrder: InstallCodeState[] = ['live', 'consumed', 'expired', 'revoked', 'none'];
const entitlementOrder: EntitlementFacet[] = ['stripe', 'comp', 'none'];
const licenseOrder: LicenseHealth[] = ['ok', 'soon', 'expired', 'none'];

// Hover explanations for the filter chips.
const statusTooltip: Record<ApplianceStatus, string> = {
  registered: 'Minted with an install code — not yet installed',
  installed: 'Appliance installed — no paid check-in yet',
  active: 'Paid appliance checking in',
  suspended: 'Reversible hold — check-in stops renewing',
  cancelled: 'Terminated',
};
const editionTooltip: Record<Edition, string> = {
  essentials: 'Free tier — no entitlement or appliance',
  pro: 'Paid tier',
};
const codeTooltip: Record<InstallCodeState, string> = {
  live: 'A valid, unclaimed code exists',
  consumed: 'Latest code was redeemed',
  expired: 'Latest code expired unredeemed',
  revoked: 'Latest code superseded or revoked',
  none: 'No code on record',
};
const entitlementTooltip: Record<EntitlementFacet, string> = {
  stripe: 'Backed by a Stripe subscription',
  comp: 'Operator-granted, time-boxed Pro with no subscription',
  none: 'Essentials — no entitlement',
};
const licenseTooltip: Record<LicenseHealth, string> = {
  ok: 'License token valid for more than 7 days',
  soon: 'License token expires within 7 days',
  expired: 'License token has expired',
  none: 'No license token issued',
};

// ── CSV export ────────────────────────────────────────────────────────────────

const csvColumns: Array<[string, (r: ApplianceListRow) => string]> = [
  ['Company', (r) => r.company_name],
  ['Contact email', (r) => r.contact_email],
  ['Tenant ID', (r) => r.tenant_id],
  ['Edition', (r) => r.edition],
  ['Tier', (r) => r.tier ?? ''],
  ['Product', (r) => r.product_code],
  ['Status', (r) => r.status],
  ['Seats', (r) => (r.seats == null ? '' : String(r.seats))],
  ['Entitlement', (r) => r.entitlement_kind ?? ''],
  ['Entitlement active', (r) => (r.entitlement_active == null ? '' : r.entitlement_active ? 'yes' : 'no')],
  ['Comp ends', (r) => (r.comp_ends_at == null ? '' : new Date(r.comp_ends_at * 1000).toISOString())],
  ['License expires', (r) => (r.license_exp == null ? '' : new Date(r.license_exp * 1000).toISOString())],
  ['Appliances', (r) => String(r.appliance_count)],
  ['Connected', (r) => (r.connected ? 'yes' : 'no')],
  ['Last check-in', (r) => r.last_checkin_at ?? ''],
  ['Install code state', (r) => r.install_code_state],
  ['Registered', (r) => r.registered_at],
  ['Installed', (r) => r.installed_at ?? ''],
];

function toCsv(rows: ApplianceListRow[]): string {
  const esc = (s: string) => `"${s.replace(/"/g, '""')}"`;
  const header = csvColumns.map(([label]) => esc(label)).join(',');
  const lines = rows.map((r) => csvColumns.map(([, get]) => esc(get(r))).join(','));
  return [header, ...lines].join('\r\n');
}

function downloadCsv(rows: ApplianceListRow[]): void {
  const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `appliance-installs-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// ── Data: fetch every install by walking the cursor ──────────────────────────

type LoadState = 'idle' | 'loading' | 'ready' | 'error';
const PAGE_LIMIT = 200; // server caps at 200 per page
const MAX_ROWS = 10000; // safety valve so a runaway cursor can never hang the UI

interface InstallsData {
  rows: ApplianceListRow[];
  state: LoadState;
  error: string | null;
  loadedCount: number;
  truncated: boolean;
  reload: () => void;
}

function useAllInstalls(bridge: IframeBridge): InstallsData {
  const [rows, setRows] = useState<ApplianceListRow[]>([]);
  const [state, setState] = useState<LoadState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [loadedCount, setLoadedCount] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const runRef = useRef(0);

  const load = useCallback(async () => {
    const run = ++runRef.current;
    setState('loading');
    setError(null);
    setTruncated(false);
    setLoadedCount(0);

    const acc: ApplianceListRow[] = [];
    let cursor: string | null = null;
    try {
      do {
        const qs = new URLSearchParams();
        qs.set('limit', String(PAGE_LIMIT));
        if (cursor) qs.set('cursor', cursor);
        const res = await callHandlerJson<ListEnvelope>(bridge, `/installs?${qs.toString()}`, { method: 'GET' });
        if (runRef.current !== run) return; // superseded by a newer reload
        if (!res?.success) throw new Error(res?.error || 'Failed to load installs');
        // Older proxies omit the Phase 2 fields; default them so facets stay stable.
        acc.push(...(res.data ?? []).map((r) => ({
          ...r,
          entitlement_kind: r.entitlement_kind ?? (r.tier ? 'stripe' : null),
          comp_ends_at: r.comp_ends_at ?? null,
          license_exp: r.license_exp ?? null,
        })));
        cursor = res.next_cursor;
        setRows(acc.slice());
        setLoadedCount(acc.length);
        if (acc.length >= MAX_ROWS) { setTruncated(true); break; }
      } while (cursor);
      if (runRef.current !== run) return;
      setState('ready');
    } catch (e) {
      if (runRef.current !== run) return;
      setError(e instanceof Error ? e.message : String(e));
      setState('error');
    }
  }, [bridge]);

  useEffect(() => { void load(); }, [load]);

  return { rows, state, error, loadedCount, truncated, reload: load };
}

// ── Filter chips ─────────────────────────────────────────────────────────────

// A compact chip that toggles a filter facet, with a hover tooltip explaining it.
function FilterChip({ label, count, tone, active, tooltip, onClick }: {
  label: string; count: number; tone?: BadgeTone; active: boolean; tooltip?: string; onClick: () => void;
}) {
  const chip = (
    <Card
      role="button"
      tabIndex={0}
      aria-pressed={active}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      style={{
        cursor: 'pointer', padding: '3px 10px', borderRadius: 999, boxShadow: 'none',
        display: 'inline-flex', alignItems: 'center', gap: 6,
        // Explicit, identical border in every state (only the colour changes when
        // active) so all chips read consistently — Card's default border + our
        // active ring otherwise made selected chips look heavier than the rest.
        border: `1px solid ${active ? 'var(--alga-primary, #8a4dea)' : 'var(--alga-border, #e2e8f0)'}`,
        background: active ? 'var(--alga-primary-soft, #f6f0fe)' : 'var(--alga-card-bg, #fff)',
      }}
    >
      {tone && <span style={{ width: 7, height: 7, borderRadius: 999, background: toneColor[tone], display: 'inline-block', flexShrink: 0 }} />}
      <Text size="sm" weight={active ? 600 : 400}>{label}</Text>
      <Text size="sm" weight={600} tone="muted">{count}</Text>
    </Card>
  );
  // ui-kit's Tooltip clones its child with a ref; Card doesn't forward refs, so
  // wrap the chip in a span (which does) to anchor the tooltip correctly.
  return tooltip ? <Tooltip content={tooltip}><span style={{ display: 'inline-flex' }}>{chip}</span></Tooltip> : chip;
}

// One labelled filter section: a fixed-width label with its chips wrapping beside it.
function FacetRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack direction="row" gap={10} align="flex-start" style={{ flexWrap: 'wrap' }}>
      <Text size="xs" tone="muted" weight={600} style={{ width: 88, flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.05em', paddingTop: 5 }}>{label}</Text>
      <Stack direction="row" gap={6} style={{ flexWrap: 'wrap', flex: 1, minWidth: 0 }}>{children}</Stack>
    </Stack>
  );
}

// ── Installs list view ───────────────────────────────────────────────────────

interface Facets {
  status?: ApplianceStatus;
  edition?: Edition;
  health?: HealthClass;
  code?: InstallCodeState;
  entitlement?: EntitlementFacet;
  license?: LicenseHealth;
}

function InstallsList({ bridge, data, onSelect }: { bridge: IframeBridge; data: InstallsData; onSelect: (id: string) => void }) {
  const { rows, state, error, loadedCount, truncated, reload } = data;
  const [search, setSearch] = useState('');
  const [facets, setFacets] = useState<Facets>({});
  const [creating, setCreating] = useState(false);

  // Rows within the current text search (facet counts narrow with search but not
  // with facet clicks, so the numbers stay stable while you toggle facets).
  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      r.company_name.toLowerCase().includes(q) ||
      r.contact_email.toLowerCase().includes(q) ||
      r.tenant_id.toLowerCase().includes(q));
  }, [rows, search]);

  const counts = useMemo(() => {
    const status: Partial<Record<ApplianceStatus, number>> = {};
    const edition: Partial<Record<Edition, number>> = {};
    const health: Partial<Record<HealthClass, number>> = {};
    const code: Partial<Record<InstallCodeState, number>> = {};
    const entitlement: Partial<Record<EntitlementFacet, number>> = {};
    const license: Partial<Record<LicenseHealth, number>> = {};
    for (const r of searched) {
      status[r.status] = (status[r.status] ?? 0) + 1;
      edition[r.edition] = (edition[r.edition] ?? 0) + 1;
      const h = healthClass(r);
      health[h] = (health[h] ?? 0) + 1;
      code[r.install_code_state] = (code[r.install_code_state] ?? 0) + 1;
      const e = entitlementFacet(r);
      entitlement[e] = (entitlement[e] ?? 0) + 1;
      const l = licenseHealth(r.license_exp);
      license[l] = (license[l] ?? 0) + 1;
    }
    return { status, edition, health, code, entitlement, license };
  }, [searched]);

  const filtered = useMemo(() => searched.filter((r) =>
    (!facets.status || r.status === facets.status) &&
    (!facets.edition || r.edition === facets.edition) &&
    (!facets.health || healthClass(r) === facets.health) &&
    (!facets.code || r.install_code_state === facets.code) &&
    (!facets.entitlement || entitlementFacet(r) === facets.entitlement) &&
    (!facets.license || licenseHealth(r.license_exp) === facets.license),
  ), [searched, facets]);

  const noFacets = Object.values(facets).every((v) => !v);
  const toggle = <K extends keyof Facets>(key: K, val: Facets[K]) =>
    setFacets((f) => ({ ...f, [key]: f[key] === val ? undefined : val }));

  // Remount the table when the filter set changes so pagination resets to page 1.
  const filterKey = `${search}|${facets.status ?? ''}|${facets.edition ?? ''}|${facets.health ?? ''}|${facets.code ?? ''}|${facets.entitlement ?? ''}|${facets.license ?? ''}`;

  const columns: Column<ApplianceListRow>[] = useMemo(() => [
    {
      key: 'company_name',
      header: 'Company',
      sortable: true,
      alwaysShow: true,
      priority: 1,
      render: (r) => (
        <Stack gap={2}>
          <Text size="sm" weight={600}>{r.company_name}</Text>
          <Text size="xs" tone="muted">{r.contact_email}</Text>
        </Stack>
      ),
    },
    {
      key: 'edition',
      header: 'Edition',
      sortable: true,
      priority: 4,
      render: (r) => (
        <Stack direction="row" gap={6} align="center" style={{ flexWrap: 'wrap' }}>
          <Text size="sm">{r.edition}</Text>
          {r.entitlement_kind === 'comp' && (
            <Tooltip content={`Comp Pro ends ${fmtUnixDate(r.comp_ends_at)}`}>
              <span style={{ display: 'inline-flex' }}><Badge tone="info">comp until {fmtUnixDate(r.comp_ends_at)}</Badge></span>
            </Tooltip>
          )}
        </Stack>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      priority: 2,
      render: (r) => <Badge tone={statusTone[r.status]}>{r.status}</Badge>,
    },
    {
      key: 'license_exp',
      header: 'License',
      sortable: true,
      priority: 3,
      render: (r) => <LicenseExpBadge exp={r.license_exp} />,
    },
    {
      key: 'seats',
      header: 'Seats',
      sortable: true,
      priority: 6,
      render: (r) => <Text size="sm">{r.seats ?? '—'}</Text>,
    },
    {
      key: 'last_checkin_at',
      header: 'Check-in',
      sortable: true,
      priority: 3,
      render: (r) => {
        const health = checkinHealth(r);
        const airgap = r.edition !== 'essentials' && !r.connected;
        const badge = <Badge tone={health.tone}>{health.label}</Badge>;
        return (
          <Stack direction="row" gap={6} align="center">
            {r.last_checkin_at
              ? <Tooltip content={fmtDateTime(r.last_checkin_at)}><span style={{ display: 'inline-flex' }}>{badge}</span></Tooltip>
              : badge}
            {airgap && <Text size="xs" tone="muted">airgap</Text>}
          </Stack>
        );
      },
    },
    {
      key: 'install_code_state',
      header: 'Install code',
      sortable: true,
      priority: 5,
      render: (r) => <Badge tone={codeTone[r.install_code_state]}>{r.install_code_state}</Badge>,
    },
    {
      key: 'registered_at',
      header: 'Generated',
      sortable: true,
      alwaysShow: true,
      priority: 3,
      render: (r) => (
        <Tooltip content={`Registered ${fmtDateTime(r.registered_at)} · ${relTime(r.registered_at)}`}>
          <span style={{ display: 'inline-flex' }}><Text size="sm">{fmtDate(r.registered_at)}</Text></span>
        </Tooltip>
      ),
    },
  ], []);

  const loading = state === 'loading';

  return (
    <Stack gap={16}>
      <Stack direction="row" gap={12} align="center" justify="space-between" style={{ flexWrap: 'wrap' }}>
        <Stack gap={2}>
          <Text as="strong" size="lg" weight={600}>Appliance Installs</Text>
          <Text size="sm" tone="muted">Self-hosted appliance tenants — list, inspect and manage installs.</Text>
        </Stack>
        <Stack direction="row" gap={8} align="center" style={{ flexWrap: 'wrap' }}>
          <SearchInput
            placeholder="Search company, email or tenant id…"
            debounceMs={200}
            showClear
            onSearch={(value) => setSearch(value)}
            style={{ width: 260 }}
          />
          <Button variant="outline" onClick={() => downloadCsv(filtered)} disabled={filtered.length === 0}>Export CSV</Button>
          <Button variant="outline" onClick={() => reload()} disabled={loading}>Refresh</Button>
          <Button variant="primary" onClick={() => setCreating(true)}>Create tenant</Button>
        </Stack>
      </Stack>

      {creating && <CreateTenantDialog bridge={bridge} isOpen onClose={() => setCreating(false)} onDone={() => reload()} />}

      {/* Filter sections */}
      {rows.length > 0 && (
        <Card style={{ padding: '12px 16px' }}>
          <Stack gap={10}>
            <Stack direction="row" gap={12} align="center" justify="space-between" style={{ flexWrap: 'wrap' }}>
              <Stack direction="row" gap={10} align="baseline" style={{ flexWrap: 'wrap' }}>
                <Text size="xs" tone="muted" weight={600} style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>Filters</Text>
                <Text size="md" weight={700}>
                  {filtered.length === rows.length ? `${rows.length} installs` : `${filtered.length} of ${rows.length} installs`}
                </Text>
                {loading && <Text size="xs" tone="muted">loading…</Text>}
              </Stack>
              {!noFacets && <Button variant="ghost" size="xs" onClick={() => setFacets({})}>Clear all</Button>}
            </Stack>
            <FacetRow label="Status">
              {statusOrder.filter((s) => counts.status[s]).map((s) => (
                <FilterChip key={s} label={s} count={counts.status[s]!} tone={statusTone[s]} tooltip={statusTooltip[s]}
                  active={facets.status === s} onClick={() => toggle('status', s)} />
              ))}
            </FacetRow>
            <FacetRow label="Edition">
              {editionOrder.filter((e) => counts.edition[e]).map((e) => (
                <FilterChip key={e} label={e} count={counts.edition[e]!} tooltip={editionTooltip[e]}
                  active={facets.edition === e} onClick={() => toggle('edition', e)} />
              ))}
            </FacetRow>
            <FacetRow label="Entitlement">
              {entitlementOrder.filter((e) => counts.entitlement[e]).map((e) => (
                <FilterChip key={e} label={e} count={counts.entitlement[e]!} tone={e === 'stripe' ? 'success' : e === 'comp' ? 'info' : 'default'} tooltip={entitlementTooltip[e]}
                  active={facets.entitlement === e} onClick={() => toggle('entitlement', e)} />
              ))}
            </FacetRow>
            <FacetRow label="License">
              {licenseOrder.filter((l) => counts.license[l]).map((l) => (
                <FilterChip key={l} label={licenseHealthMeta[l].label} count={counts.license[l]!} tone={licenseHealthMeta[l].tone} tooltip={licenseTooltip[l]}
                  active={facets.license === l} onClick={() => toggle('license', l)} />
              ))}
            </FacetRow>
            <FacetRow label="Check-in">
              {healthOrder.filter((h) => counts.health[h]).map((h) => (
                <FilterChip key={h} label={healthFacetMeta[h].label} count={counts.health[h]!} tone={healthFacetMeta[h].tone} tooltip={healthFacetMeta[h].desc}
                  active={facets.health === h} onClick={() => toggle('health', h)} />
              ))}
            </FacetRow>
            <FacetRow label="Install code">
              {codeOrder.filter((c) => counts.code[c]).map((c) => (
                <FilterChip key={c} label={c} count={counts.code[c]!} tone={codeTone[c]} tooltip={codeTooltip[c]}
                  active={facets.code === c} onClick={() => toggle('code', c)} />
              ))}
            </FacetRow>
          </Stack>
        </Card>
      )}

      {error && (
        <Alert tone="danger">
          <Stack direction="row" gap={12} align="center" justify="space-between" style={{ flexWrap: 'wrap' }}>
            <Text size="sm">{error}</Text>
            <Button variant="outline" size="sm" onClick={() => reload()}>Retry</Button>
          </Stack>
        </Alert>
      )}

      {loading && rows.length === 0 && (
        <Card><LoadingIndicator text={`Loading appliance installs… ${loadedCount} loaded`} /></Card>
      )}

      {truncated && (
        <Alert tone="warning"><Text size="sm">Showing the first {MAX_ROWS} installs — result set truncated.</Text></Alert>
      )}

      {state === 'ready' && rows.length === 0 && !error && (
        <Alert tone="info"><Text size="sm">No appliance installs found.</Text></Alert>
      )}

      {rows.length > 0 && filtered.length === 0 && (
        <Alert tone="info">
          <Stack direction="row" gap={12} align="center" justify="space-between" style={{ flexWrap: 'wrap' }}>
            <Text size="sm">No installs match the current filters.</Text>
            <Button variant="outline" size="sm" onClick={() => { setFacets({}); setSearch(''); }}>Clear filters</Button>
          </Stack>
        </Alert>
      )}

      {filtered.length > 0 && (
        <Stack gap={8}>
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <DataTable
              key={filterKey}
              columns={columns}
              data={filtered}
              initialSortKey="company_name"
              onRowClick={(r) => onSelect(r.tenant_id)}
              responsiveColumns
              paginate
              defaultPageSize={25}
            />
          </Card>
        </Stack>
      )}
    </Stack>
  );
}

// ── Root ─────────────────────────────────────────────────────────────────────

// The runner serves this iframe without the ?parentOrigin= param, so resolve the host
// origin via document.referrer before our own (cross-origin) origin — same fallback the
// Control Panel uses. Without it the SDK's default breaks the postMessage bridge.
function getParentOrigin(): string {
  const fromParam = new URLSearchParams(window.location.search).get('parentOrigin');
  if (fromParam) return fromParam;
  if (document.referrer) {
    try {
      return new URL(document.referrer).origin;
    } catch {
      /* invalid referrer */
    }
  }
  return window.location.origin;
}

function useHostBridge(): IframeBridge {
  const bridge = useMemo(() => new IframeBridge({ expectedParentOrigin: getParentOrigin() }), []);
  useEffect(() => { bridge.ready(); }, [bridge]);
  return bridge;
}

/** Detect whether a hex color is dark, so ui-kit's `[data-theme="dark"]` fallbacks engage. */
function isDarkColor(hex: string): boolean {
  const h = hex.replace('#', '');
  if (h.length !== 6) return false;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 0.5;
}

function App() {
  const bridge = useHostBridge();
  const theme = useTheme(bridge);
  const [selected, setSelected] = useState<string | null>(null);
  // The list's data lives here so a detail action can refresh the rows behind it.
  const installs = useAllInstalls(bridge);

  // Apply host theme tokens to this iframe's :root, and set data-theme so the
  // ui-kit token defaults switch light/dark even when the host omits a token.
  useEffect(() => {
    if (!theme) return;
    const root = document.documentElement;
    for (const [k, v] of Object.entries(theme)) root.style.setProperty(k, v);
    const bg = theme['--alga-bg'];
    if (bg && /^#[0-9a-fA-F]{6}$/.test(bg)) {
      root.setAttribute('data-theme', isDarkColor(bg) ? 'dark' : 'light');
    }
  }, [theme]);

  // Log console access once (audit trail). Fire-and-forget.
  useEffect(() => {
    void callHandlerJson(bridge, '/access', { method: 'POST', body: { view: 'console' } }).catch(() => undefined);
  }, [bridge]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        fontFamily: 'system-ui, sans-serif',
        color: 'var(--alga-fg)',
        background: 'var(--alga-bg)',
      }}
    >
      {/* Scroll lives here: a fixed-height flex child that owns its overflow, so the
          content scrolls inside the host's clamped iframe instead of being clipped.
          The list stays mounted (hidden) while a detail is open so its fetched rows,
          search, filters, sort, and page all survive the round trip. */}
      <main style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: 20 }}>
          <div style={{ display: selected ? 'none' : 'block' }}>
            <InstallsList bridge={bridge} data={installs} onSelect={setSelected} />
          </div>
          {selected && (
            <InstallDetail bridge={bridge} tenantId={selected} onBack={() => setSelected(null)} onChanged={installs.reload} />
          )}
        </div>
      </main>
    </div>
  );
}

const rootEl = document.getElementById('root');
if (rootEl) createRoot(rootEl).render(<App />);
